import { AXIOS_CONFIG } from '@/lib/Apis/wrapper';
import { ProductsApi } from '@/lib/Apis';
import { ProductFilters } from '@/lib/types';
import { useQuery } from '@tanstack/react-query';

export enum SortOrder {
  Asc = "asc",
  Desc = "desc",
}

export function useProducts(filters?: ProductFilters, page: number = 1, limit: number = 20) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      "products",
      filters?.search,
      filters?.category,
      filters?.subCategory,
      filters?.inStock,
      page,
      limit
    ],
    queryFn: async () => {
      const response = await new ProductsApi(AXIOS_CONFIG).getAllProducts(
        undefined, // maxPrice — omitted; price filtering is handled client-side to correctly support multi-variant products
        undefined, // minPrice
        undefined, // brandFilter
        filters?.inStock !== undefined ? (filters.inStock ? 'in-stock' : 'out-of-stock') : undefined, // availability
        "createdAt", // sort
        filters?.subCategory, // subCategoryId
        filters?.category, // categoryId
        true, // isActive
        limit, // limit
        page // page
      );

      const res = response.data;

      return {
        products: res.data || [],
        pagination: {
          page: res.currentPage || page,
          limit: res.limit || limit,
          total: res.totalItems || 0,
          totalPages: res.totalPages || 0,
        },
      };
    },
    staleTime: 2 * 60 * 1000, // 2 minutes - products list can be cached briefly
  });


  return {
    products: data?.products || [],
    isLoading,
    error: error as Error | null,
    pagination: data?.pagination || {
      page: 1,
      limit: 20,
      total: 0,
      totalPages: 0,
    },
    refetch,
  };
}

export function useProduct(id: string) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['product', id],
    queryFn: async () => {
      const response = await new ProductsApi(AXIOS_CONFIG).getSingleProduct(id);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - single product details rarely change
    enabled: !!id, // Only fetch if id is provided
  });

  return {
    product: data || null,
    isLoading,
    error: error as Error | null,
    refetch,
  };
}

export function useHomepageSpotlightProduct() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['homepage-spotlight-product'],
    queryFn: async () => {
      // The compatible `/products/featured` endpoint now resolves the product
      // explicitly selected through `homeScreenSpotlight`.
      const response = await new ProductsApi(AXIOS_CONFIG).getFeaturedProduct();
      return response.data;
    },
    // Spotlight is controlled from a separate admin app. Always refresh when
    // shoppers return to the page, and poll lightly while it remains open so
    // removing the spotlight also removes the component without a hard reload.
    staleTime: 0,
    refetchOnWindowFocus: 'always',
    refetchInterval: 15_000,
  });

  return {
    spotlightProduct: data ?? null,
    isLoading,
    error: error as Error | null,
  };
}

/**
 * @deprecated Use `useHomepageSpotlightProduct` for the explicit single-select
 * homepage product. Kept as an alias for existing storefront integrations.
 */
export function useFeaturedProducts() {
  const { spotlightProduct, isLoading, error } = useHomepageSpotlightProduct();

  return {
    featuredProduct: spotlightProduct,
    isLoading,
    error,
  };
}

// Re-export useCategories from CategoriesProvider for backward compatibility
export { useCategories } from '@/providers/CategoriesProvider';
